import { MailOutlined, UserAddOutlined } from "@ant-design/icons"
import { Button, Col, Input, Modal, Row, Select, Space, Typography } from "antd"
import { addDoc, collection, serverTimestamp } from "firebase/firestore"
import { useState } from "react"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"
import { Notification } from "../Notification"
import { CountryCodesSelect, countries } from "./CountryCodesSelect"
import { checkVatExists, normalizeVat } from "../../utils/VatChecker"

const { Text } = Typography
const { TextArea } = Input

const phoneOptions = countries.map((c) => ({
  value: c.phoneEx,
  label: c.phoneEx,
}))

export default function CreateLeadPopup({ icon }) {
  const { user } = useAuthContext()

  const [isModalOpen, setIsModalOpen] = useState(false)

  // Form state
  const [companyName, setCompanyName] = useState("")
  const [vat, setVat] = useState("")
  const [reg, setReg] = useState("")
  const [contactPerson, setContactPerson] = useState("")
  const [phoneCode, setPhoneCode] = useState("+371")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [selectedCountry, setSelectedCountry] = useState("Latvia")
  const [selectedCountryCode, setSelectedCountryCode] = useState("LV")
  const [comments, setComments] = useState("")

  // Validation flags
  const [companyError, setCompanyError] = useState(false)
  const [contactError, setContactError] = useState(false)
  const [phoneError, setPhoneError] = useState(false)
  const [emailError, setEmailError] = useState(false)

  const [loading, setLoading] = useState(false)

  const showModal = () => setIsModalOpen(true)
  const handleCancel = () => resetForm()

  const resetForm = () => {
    setCompanyName("")
    setVat("")
    setReg("")
    setContactPerson("")
    setPhoneCode("+371")
    setPhone("")
    setEmail("")
    setSelectedCountry("Latvia")
    setSelectedCountryCode("LV")
    setComments("")
    setCompanyError(false)
    setContactError(false)
    setPhoneError(false)
    setEmailError(false)
    setIsModalOpen(false)
    setLoading(false)
  }

  const handleOk = async (e) => {
    e.preventDefault()
    setLoading(true)

    let valid = true
    if (companyName.trim().length === 0) { setCompanyError(true); valid = false }
    if (contactPerson.trim().length === 0) { setContactError(true); valid = false }
    if (phone.trim().length === 0) { setPhoneError(true); valid = false }
    if (email.trim().length === 0) { setEmailError(true); valid = false }

    if (!valid) {
      setLoading(false)
      return
    }

    try {
      let vatNormalized = ""
      if (vat.trim().length > 0) {
        vatNormalized = normalizeVat(selectedCountryCode, vat)
        const dup = await checkVatExists(vatNormalized)
        if (dup.exists) {
          Notification(
            "error",
            "Duplicate VAT",
            `This VAT already exists in ${dup.where} as "${dup.name || dup.id}".`
          )
          setLoading(false)
          return
        }
      }

      const ref = collection(db, "leads")
      await addDoc(ref, {
        client: companyName,
        vat: vatNormalized,
        reg,
        contactPerson,
        phone: `${phoneCode}${phone}`,
        email,
        country: selectedCountry,
        comments,
        user: user.uid,
        userEmail: user.email,
        createdAt: serverTimestamp(),
        profitability: 34,
        approved: true,
        lead: true,
      })
      Notification("success", "Lead Added", "You have successfully added a new lead!")
      resetForm()
    } catch (err) {
      console.error("Error creating lead:", err)
      Notification("error", "Creation Error", err.message)
      setLoading(false)
    }
  }

  const returnButtonOrIcon = () => {
    if (icon) {
      return (
        <Button
          onClick={showModal}
          type="primary"
          icon={<UserAddOutlined style={{ color: "white" }} />}
        />
      )
    } else {
      return (
        <Button type="primary" onClick={showModal}>
          Create Lead
        </Button>
      )
    }
  }

  const requiredLabel = (label) => (
    <Space size="small">
      <Text>{label}</Text>
      <Text italic style={{ color: "red", fontSize: "10px" }}>
        (required)
      </Text>
    </Space>
  )

  return (
    <>
      {returnButtonOrIcon()}

      <Modal
        title="Create Lead"
        open={isModalOpen}
        onOk={handleOk}
        okButtonProps={{ loading }}
        okText="Create Lead"
        onCancel={handleCancel}
        width={600}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Row gutter={[16, 0]}>
            <Col span={12}>
              {requiredLabel("Company Name")}
              <Input
                status={companyError ? "error" : ""}
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value)
                  setCompanyError(false)
                }}
                placeholder="Enter company name"
              />
            </Col>
            <Col span={12}>
              <Text>VAT Number</Text>
              <Input
                prefix={selectedCountryCode}
                value={vat}
                onChange={(e) => setVat(e.target.value)}
                placeholder="Enter VAT number (optional)"
              />
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Text>Registration Number</Text>
              <Input
                value={reg}
                onChange={(e) => setReg(e.target.value)}
                placeholder="Enter registration number (optional)"
              />
            </Col>
            <Col span={12}>
              {requiredLabel("Contact Person")}
              <Input
                status={contactError ? "error" : ""}
                value={contactPerson}
                onChange={(e) => {
                  setContactPerson(e.target.value)
                  setContactError(false)
                }}
                placeholder="Enter contact person name"
              />
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              {requiredLabel("Phone Number")}
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  value={phoneCode}
                  onChange={setPhoneCode}
                  style={{ width: "38%" }}
                  showSearch
                  optionFilterProp="label"
                  options={phoneOptions}
                />
                <Input
                  status={phoneError ? "error" : ""}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value)
                    setPhoneError(false)
                  }}
                  placeholder="Phone number"
                />
              </Space.Compact>
            </Col>
            <Col span={12}>
              {requiredLabel("Email")}
              <Input
                status={emailError ? "error" : ""}
                prefix={<MailOutlined />}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError(false)
                }}
                placeholder="Enter email address"
              />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              {requiredLabel("Country")}
              <CountryCodesSelect
                defaultCountry={selectedCountry}
                setSelectedCountryCode={setSelectedCountryCode}
                setSelectedCountry={setSelectedCountry}
                setSelectedPhoneEx={setPhoneCode}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Text>Comments</Text>
              <TextArea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional comments..."
                rows={4}
                showCount
                maxLength={500}
              />
            </Col>
          </Row>

          {(companyError || contactError || phoneError || emailError) && (
            <Row>
              <Col>
                <Text italic style={{ color: "red" }}>
                  Please fill out the required fields!
                </Text>
              </Col>
            </Row>
          )}
        </Space>
      </Modal>
    </>
  )
}
